
import React, { useContext, useState } from 'react';
import { TaskContext } from '../contexts/TaskContext';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { 
  List, 
  ListItem, 
  ListItemText, 
  TextField, 
  Button, 
  Paper, 
  Typography, 
  Box,
  Chip,
  ListItemButton,
  Menu,
  MenuItem,
  ListItemIcon
} from '@mui/material';
import { Add, DragIndicator, Delete, CheckCircle, PlayArrow, RemoveCircle } from '@mui/icons-material';

const TaskList = () => {
  const { tasks, addTask, reorderTasks, selectTask, selectedTask, deleteTask, updateTaskStatus } = useContext(TaskContext);
  const [newTaskName, setNewTaskName] = useState('');
  const [contextMenu, setContextMenu] = useState({ open: false, x: 0, y: 0, task: null });

  const handleAddTask = () => {
    if (newTaskName.trim()) {
      addTask(newTaskName.trim());
      setNewTaskName('');
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleAddTask();
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    reorderTasks(result.source.index, result.destination.index);
  };

  const handleRightClick = (event, task) => {
    event.preventDefault();
    setContextMenu({
      open: true,
      x: event.clientX,
      y: event.clientY,
      task: task
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu({ open: false, x: 0, y: 0, task: null });
  };

  const handleStatusChange = (status) => {
    if (contextMenu.task) {
      updateTaskStatus(contextMenu.task.id, status);
    }
    handleCloseContextMenu();
  };

  const handleDeleteTask = () => {
    if (contextMenu.task) {
      deleteTask(contextMenu.task.id);
    }
    handleCloseContextMenu();
  };


  const activeTasks = tasks
    .filter(task => !task.completed && !task.removed)
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  const completedTasks = tasks.filter(task => task.completed && !task.removed);

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Tasks
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          label="Add new task"
          value={newTaskName}
          onChange={(e) => setNewTaskName(e.target.value)}
          onKeyPress={handleKeyPress}
          size="small"
          fullWidth
        />
        <Button 
          onClick={handleAddTask} 
          variant="contained" 
          startIcon={<Add />}
          disabled={!newTaskName.trim()}
        >
          Add
        </Button>
      </Box>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="tasks">
          {(provided, snapshot) => (
            <List 
              {...provided.droppableProps} 
              ref={provided.innerRef}
              sx={{ 
                bgcolor: snapshot.isDraggingOver ? 'action.hover' : 'inherit',
                minHeight: 200,
                borderRadius: 1
              }}
            >
              {activeTasks.map((task, index) => (
                <Draggable key={task.id} draggableId={task.id} index={index}>
                  {(provided, snapshot) => (
                    <ListItem
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      disablePadding
                      sx={{ 
                        mb: 1,
                        borderRadius: 1,
                        bgcolor: selectedTask?.id === task.id ? 'primary.light' : 'background.paper',
                        '&:hover': { bgcolor: 'action.hover' },
                        boxShadow: snapshot.isDragging ? 2 : 0
                      }}
                    >
                      <ListItemButton 
                        onClick={() => selectTask(task.id)}
                        onContextMenu={(e) => handleRightClick(e, task)}
                        sx={{ pl: 1 }}
                      >
                        <Box 
                          {...provided.dragHandleProps}
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            mr: 1,
                            color: 'text.secondary'
                          }}
                        >
                          <DragIndicator />
                        </Box>
                        <ListItemText 
                          primary={task.name}
                          sx={{ 
                            color: selectedTask?.id === task.id ? 'primary.contrastText' : 'inherit'
                          }}
                        />
                        {selectedTask?.id === task.id && (
                          <Chip 
                            label="Current" 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                        )}
                      </ListItemButton>
                    </ListItem>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
              {activeTasks.length === 0 && (
                <ListItem>
                  <ListItemText 
                    primary="No active tasks" 
                    primaryTypographyProps={{ 
                      color: 'textSecondary',
                      align: 'center',
                      fontStyle: 'italic'
                    }}
                  />
                </ListItem>
              )}
            </List>
          )}
        </Droppable>
      </DragDropContext>

      {completedTasks.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            Completed ({completedTasks.length})
          </Typography>
          <List dense>
            {completedTasks.slice(-5).map((task) => (
              <ListItem key={task.id} sx={{ pl: 0 }}>
                <ListItemText 
                  primary={task.name}
                  primaryTypographyProps={{ 
                    sx: { textDecoration: 'line-through', color: 'text.secondary' }
                  }}
                />
                <Chip label="Done" size="small" color="success" variant="outlined" />
              </ListItem>
            ))}
            {completedTasks.length > 5 && (
              <ListItem sx={{ pl: 0 }}>
                <ListItemText 
                  primary={`... and ${completedTasks.length - 5} more completed tasks`}
                  primaryTypographyProps={{ 
                    color: 'textSecondary',
                    fontStyle: 'italic'
                  }}
                />
              </ListItem>
            )}
          </List>
        </Box>
      )}

      <Menu
        open={contextMenu.open}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu.open
            ? { top: contextMenu.y, left: contextMenu.x }
            : undefined
        }
      >
        <MenuItem onClick={() => handleStatusChange('New')}>
          <ListItemIcon>
            <RemoveCircle fontSize="small" />
          </ListItemIcon>
          Mark as New
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange('Started')}>
          <ListItemIcon>
            <PlayArrow fontSize="small" />
          </ListItemIcon>
          Mark as Started
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange('Completed')}>
          <ListItemIcon>
            <CheckCircle fontSize="small" />
          </ListItemIcon>
          Mark as Completed
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange('Removed')}>
          <ListItemIcon>
            <RemoveCircle fontSize="small" />
          </ListItemIcon>
          Mark as Removed
        </MenuItem>
        <MenuItem onClick={handleDeleteTask} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <Delete fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          Delete Task
        </MenuItem>
      </Menu>
    </Paper>
  );
};

export default TaskList;
